import {
  ShiftManagementBulkCreateShiftsDocument,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import { Circle } from '@factorialco/f0-react/icons/app'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import type { GridSelection } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useAddRestDays } from 'modules/shift_management/hooks/useAddRestDays'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectionDispatch } from 'modules/shift_management/hooks/useSelection'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import buildAddRestDaysParameters from 'modules/shift_management/lib/buildAddRestDaysParameters'
import { buildAddShiftsInputParameters } from 'modules/shift_management/lib/buildAddShiftsInput'
import { shiftMutationFeedback } from 'modules/shift_management/lib/handleShiftMutationFeedback'
import type { Shift, View } from 'modules/shift_management/lib/types'
import ShiftModel from 'modules/shift_management/store/internal/models/Shift'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

interface Props {
  daysByRow: Record<string, string[]>
  hasMore: boolean
  onCopy: () => void
  onPaste: () => void
  selectionKind?: GridSelection['kind']
  selectedShifts: Record<string, Shift>
  view: View
  visibleDefaultShifts: {
    active: DefaultShift[]
  }
}

const useShiftTableFormatBar = ({
  daysByRow,
  hasMore,
  onCopy,
  onPaste,
  selectionKind,
  selectedShifts,
  view,
  visibleDefaultShifts,
}: Props) => {
  const dispatchSelection = useSelectionDispatch()
  const [, dispatchUI] = useUI()
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view: scheduleView }, dispatchSchedule] = useSchedule()
  const { refreshStats } = useRefreshStats({ fetchPolicy: 'cache-only' })
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const defaultShiftIds = visibleDefaultShifts.active.map((defaultShift) => defaultShift.id)
  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })

  const [addShifts] = useMutation(ShiftManagementBulkCreateShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementShift,
    update: (cache, { data }) => {
      const newShifts = data?.shiftManagementMutations.bulkCreateShiftmanagementShift.shifts
      if (!newShifts) return

      updateCreatedShiftsCache({
        cache,
        view: scheduleView,
        shifts: newShifts,
      })
    },
  })

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      removeDestroyedShiftsCache({
        cache,
        view,
        shifts: data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts || [],
      })
    },
  })

  const options = useUnsafeMemo(() => {
    return visibleDefaultShifts.active.map((defaultShift) => {
      const plannedBreaksCount = plannedBreaksCollection.active.filterByDefaultShift(
        defaultShift.id
      ).length

      return {
        value: defaultShift.id.toString(),
        label: defaultShift.title,
        icon: Circle,
        description: `${defaultShift.startAt} - ${defaultShift.endAt}`,
        tag:
          plannedBreaksCount > 0
            ? {
                color: 'yellow',
                text: plannedBreaksCount.toString(),
                type: 'dot',
              }
            : undefined,
      }
    })
  }, [visibleDefaultShifts.active, plannedBreaksCollection.active])

  const selectedValues = useUnsafeMemo(() => {
    const selectedShiftIds = Object.values(selectedShifts)
      .map((shift) => shift.defaultShiftId)
      .filter((defaultShiftId): defaultShiftId is string => Boolean(defaultShiftId))

    return Array.from(new Set(selectedShiftIds))
  }, [selectedShifts])

  const restDaysParams = useUnsafeMemo(() => {
    return buildAddRestDaysParameters(daysByRow, view)
  }, [daysByRow, view])

  const getShiftsToDestroy = (selectedShiftsList: Shift[], removedIds: string[]): Shift[] => {
    return selectedShiftsList.filter(
      (shift) => shift.defaultShiftId && removedIds.includes(shift.defaultShiftId)
    )
  }

  const getSelectionDiff = (prev: string[], next: string[]) => {
    const prevSet = new Set(prev)
    const nextSet = new Set(next)

    const added = next.filter((id) => !prevSet.has(id))
    const removed = prev.filter((id) => !nextSet.has(id))

    return { added, removed }
  }

  const handleSelect = useUnsafeCallback(
    async (value: string[] | string | null) => {
      const nextSelectedValues = value ? (Array.isArray(value) ? value : [value]) : []
      const { added: addedDefaultShiftIds, removed: removedDefaultShiftIds } = getSelectionDiff(
        selectedValues,
        nextSelectedValues
      )
      const selectedShiftsList = Object.values(selectedShifts)
      const { startFeedback, onSuccess, onError } = shiftMutationFeedback({
        progressMessage:
          removedDefaultShiftIds.length > 0
            ? i18n.t('shift_management.actions.deleting_shifts')
            : i18n.t('shift_management.actions.creating_shifts'),
        successMessage:
          removedDefaultShiftIds.length > 0
            ? i18n.t('shift_management.actions.delete_shifts_success')
            : i18n.t('shift_management.actions.add_shifts_success'),
      })
      let hasMutation = false

      if (removedDefaultShiftIds.length) {
        const shiftsToDestroy = getShiftsToDestroy(selectedShiftsList, removedDefaultShiftIds)

        if (shiftsToDestroy.length > 0) {
          startFeedback()

          const destroyRemove = await destroyShifts({
            variables: {
              ids: shiftsToDestroy.map((shift) => shift.id),
              destroyBackupShifts: true,
              destroyAutomaticShifts: false,
            },
          })
          const handledRemove = handleResult(destroyRemove, (_errors) => {})

          if (handledRemove.success) {
            hasMutation = true
          } else {
            onError()
            return
          }
        }
      }

      if (addedDefaultShiftIds.length) {
        const defaultShiftsById = new Map(
          visibleDefaultShifts.active.map((defaultShift) => [defaultShift.id, defaultShift])
        )

        const shifts = addedDefaultShiftIds.flatMap((defaultShiftId) => {
          const defaultShift = defaultShiftsById.get(defaultShiftId)
          if (!defaultShift) return []

          startFeedback()
          return buildAddShiftsInputParameters({
            view,
            hasSelection: true,
            defaultAttributesByDate: {},
            name: defaultShift.title,
            unparsedStartTime: defaultShift.startAt,
            unparsedEndTime: defaultShift.endAt,
            daysByRow,
            defaultShiftId: defaultShift.id,
            extraHours: defaultShift.extraHours,
            notes: defaultShift.notes,
            locationId: defaultShift.locationId,
            workAreaId: defaultShift.workAreaId,
            color: defaultShift.color,
            templateWeekId: null,
            unassignedShiftId: null,
            state: 'draft',
            leaveId: undefined,
            companyId: currentCompanyId,
          })
        })

        if (shifts.length > 0) {
          const result = await addShifts({ variables: { shifts } })
          const handled = handleResult(result, (_errors) => {})

          if (handled.success) {
            hasMutation = true
            const shiftsData = handled.data?.shifts || []
            const shiftsCreated = shiftsData.map((shift) =>
              ShiftModel.buildFromGraphQL({
                shift,
                employeeFullName: shift.employee?.fullName || '',
                employeeAvatar: shift.employee?.avatar?.url || '',
                employeeLocationId: shift.employee?.locationId ?? '0',
              })
            )
            dispatchUI({ type: 'REFRESHING_STATS' })
            dispatchSchedule({ type: 'ADD_SHIFTS', shifts: shiftsCreated })
          } else {
            onError()
            return
          }
        }
      }

      const affectedRowKeys = new Set<string>()
      selectedShiftsList.forEach((shift) =>
        affectedRowKeys.add(shift.rowKeyReference(scheduleView))
      )
      Object.keys(daysByRow).forEach((rowKey) => affectedRowKeys.add(rowKey))

      const previousShiftsRows = shiftsRows.filter((row) => affectedRowKeys.has(row.key))
      if (previousShiftsRows.length > 0) await refreshStats(previousShiftsRows)

      if (hasMutation) await onSuccess()

      dispatchSelection({ type: 'CLEAR_SELECTION' })
    },
    [
      addShifts,
      currentCompanyId,
      daysByRow,
      destroyShifts,
      dispatchSchedule,
      dispatchSelection,
      dispatchUI,
      refreshStats,
      scheduleView,
      selectedShifts,
      selectedValues,
      shiftsRows,
      view,
      visibleDefaultShifts.active,
    ]
  )

  const { onSubmit: onAddRestDays, submitting: isAddingRestDays } = useAddRestDays({
    params: restDaysParams,
    onSuccess: () => {
      dispatchSelection({ type: 'CLEAR_SELECTION' })
    },
  })

  const isClipboardDisabled =
    hasMore && (selectionKind === 'column' || selectionKind === 'columnRange')

  return {
    options,
    selectedValues,
    handleSelect,
    onAddRestDays,
    onCopy,
    onPaste,
    isAddingRestDays,
    isAddRestDaysDisabled: restDaysParams.length === 0,
    isCopyDisabled: isClipboardDisabled,
    isPasteDisabled: isClipboardDisabled,
  }
}

export { useShiftTableFormatBar }
